/* eslint-disable spire/export-styles */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import Trash2 from "@NA/Icons/Trash2";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import Plus from "@insite/mobius/Icons/Plus";
import Minus from "@insite/mobius/Icons/Minus";
import TextField, { TextFieldPresentationProps, TextFieldProps } from "@insite/mobius/TextField";
import React, { ChangeEvent, useEffect, useState } from "react";
import { connect, useDispatch } from "react-redux";
import { css } from "styled-components";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import getColor from "@insite/mobius/utilities/getColor";
import { AIProtonModel } from "@NA/Widgets/AIProton/AIProtonModel";
import { FormFieldStyle, FormInputWrapper } from "@insite/mobius/FormField";
import Img from "@insite/mobius/Img";
import { addOrRemoveCart } from "@NA/Utils/GtmUtility";
import { Session } from "@insite/client-framework/Services/SessionService";
import { removeProductFromCart } from "@NA/Overrides/Common/Utilities/CustomTracking";
import useDebounce from "@NA/Hooks/useDebounce";
import addToCart from "@NA/Handlers/ADIAddToCart";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import { checkAllowedQuantity } from "@NA/Utils/Utility";

interface OwnProps extends TextFieldProps {
    configurationCompleted?: boolean;
    variantSelectionCompleted?: boolean;
    labelOverride?: React.ReactNode;
    extendedStyles?: TextFieldPresentationProps;
    variant?: "standard" | "product listing grid" | "product listing list" | "product detail";
    cartLine?: any;
    cartStateLoading?: boolean;
    product: AIProtonModel;
}

// eslint-disable-next-line @typescript-eslint/class-name-casing
export interface quantityTypes {
    container?: InjectableCss;
    containerList?: InjectableCss;
    icons?: InjectableCss;
    iconsList?: InjectableCss;
    field?: InjectableCss;
    fieldList?: InjectableCss;
    loading?: InjectableCss;
    loadingList?: InjectableCss;
    fieldLoading?: InjectableCss;
    fieldLoadingList?: InjectableCss;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

const mapStateToProps = (state: CustomApplicationState) => {
    return {
        addToCartLoading: state.context.isUpdatingCart,
        updateCartLoading: state.pages.cart.isUpdatingCartLine,
        removeCartLineLoading: state.pages.cart.isRemovingCartLine,
        session: state.context.session,
        isCartLoading: getCurrentCartState(state).isLoading || state.ADIGlobalData.ADIUpdateCartLoading.loading,
    };
};

export const productQuantityOrderedStyles: TextFieldPresentationProps | quantityTypes = {
    cssOverrides: {
        formInputWrapper: css`
            input[type="number"]::-webkit-outer-spin-button,
            input[type="number"]::-webkit-inner-spin-button {
                /* ISC-11023 deal with this. */
                /* stylelint-disable-next-line */
                -webkit-appearance: none;
                margin: 0;
            }
        `,
        inputSelect: css`
            /* ISC-11023 deal with this. */
            /* stylelint-disable-next-line */
            -moz-appearance: textfield;
        `,
    },
};

const fieldStyles: quantityTypes = {
    container: {
        css: css`
            display: flex;
            width: 100%;
            border-top: 1px solid ${getColor("text.disabled")};
            margin-top: auto;
        `,
    },
    containerList: {
        css: css`
            display: flex;
            width: 100%;
            border-top: 1px solid ${getColor("text.disabled")};
            margin-top: auto;
            border-radius: 0;
            margin: 0;
            height: 63px;
            border-bottom-left-radius: 5px;
            border-bottom-right-radius: 5px;
            .minusIcon {
                :hover {
                    border-bottom-left-radius: 5px;
                }
                :active {
                    background-color: ${getColor("secondary")};
                    border-bottom-left-radius: 5px;
                    svg {
                        stroke: ${getColor("primary.contrast")};
                    }
                }
            }
            .plusIcon {
                :hover {
                    border-bottom-right-radius: 5px;
                }
                :active {
                    background-color: ${getColor("secondary")};
                    border-bottom-right-radius: 5px;
                    svg {
                        stroke: ${getColor("primary.contrast")};
                    }
                }
            }
        `,
    },
    icons: {
        css: css`
            width: 35%;
            display: flex;
            justify-content: center;
            min-height: 63px;
            align-items: center;
            cursor: pointer;
            transition: all 0.2s ease;
            svg {
                width: 20px;
                height: 20px;
                position: absolute;
            }

            :hover {
                background-color: ${getColor("primary")};

                svg {
                    stroke: ${getColor("primary.contrast")};
                }
            }
        `,
    },
    iconsList: {
        css: css`
            width: 25%;
            display: flex;
            justify-content: center;
            min-height: 35px;
            align-items: center;
            cursor: pointer;
            transition: all 0.2s ease;
            svg {
                width: 20px;
                height: 20px;
                position: absolute;
            }

            :hover {
                background-color: ${getColor("primary")};
                svg {
                    stroke: ${getColor("primary.contrast")};
                    filter: brightness(0) invert(1);
                }
            }
        `,
    },
    loading: {
        css: css`
            width: 35%;
            display: flex;
            justify-content: center;
            position: relative;
            align-items: center;
            min-height: 63px;
            cursor: pointer;
            transition: all 0.2s ease;
            pointer-events: none;
            Img {
                width: 40px;
                position: absolute;
            }
        `,
    },
    loadingList: {
        css: css`
            width: 25%;
            display: flex;
            justify-content: center;
            position: relative;
            align-items: center;
            min-height: 35px;
            cursor: pointer;
            transition: all 0.2s ease;
            pointer-events: none;
            Img {
                width: 40px;
                position: absolute;
            }
        `,
    },
    field: {
        css: css`
            width: 75%;
            > ${FormFieldStyle} {
                > ${FormInputWrapper} {
                    input {
                        font-size: 16px;
                        font-weight: bold;
                        color: #2d2926;
                        border-radius: 0;
                    }
                }
            }
            [class^="FormFieldStyle-"] {
                height: 100%;
                width: 100%;
                display: block;
            }
            [class^="FormInputWrapper-"] {
                height: 100%;

                input {
                    display: block;
                    width: 100%;
                    border-radius: 0;
                    border: 0;
                    text-align: center;
                    border-left: 1px solid ${getColor("text.disabled")} !important;
                    border-right: 1px solid ${getColor("text.disabled")} !important;
                    height: 100%;
                    border-top: 0 !important;
                    border-bottom: 0 !important;
                }
            }
        `,
    },
    fieldList: {
        css: css`
            width: 60%;
            [class^="FormFieldStyle-"] {
                height: 100%;
                width: 100%;
                display: block;
            }
            [class^="FormInputWrapper-"] {
                height: 100%;

                input {
                    display: block;
                    width: 100%;
                    border-radius: 0;
                    border: 0;
                    text-align: center;
                    border: 1px solid ${getColor("primary")};
                    height: 100%;
                    border-top: 0;
                    border-bottom: 0;
                }
            }
        `,
    },
    fieldLoading: {
        css: css`
            width: 60%;
            min-height: 63px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-left: 1px solid ${getColor("text.disabled")};
            border-right: 1px solid ${getColor("text.disabled")};
            pointer-events: none;
            border: 0;
            Img {
                width: 40px;
            }
        `,
    },
    fieldLoadingList: {
        css: css`
            width: 75%;
            min-height: 35px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-left: 1px solid ${getColor("primary")};
            border-right: 1px solid ${getColor("primary")};
            pointer-events: none;
            Img {
                width: 35px;
            }
        `,
    },
};

const ADIProductQuantityOrdered = ({
    variant = "product listing list",
    product,
    cartLine,
    extendedStyles,
    addToCartLoading,
    updateCartLoading,
    removeCartLineLoading,
    session,
    isCartLoading,
    ...otherProps
}: Props) => {
    const [styles] = useState(() => mergeToNew(productQuantityOrderedStyles, extendedStyles));
    const [productQty, setProductQty] = useState(String(cartLine.qtyOrdered));
    const [selectedProduct, setSelectedProduct] = React.useState<string>("");
    const [updateQuantityLoader, setUpdateQuantityLoader] = React.useState(false);
    const dispatch: any = useDispatch();
    const [addClicks, setAddClicks] = useState(0);
    const [minusClicks, setMinusClicks] = useState(0);
    const latestAddClicks = React.useRef(addClicks);
    const latestMinusClicks = React.useRef(minusClicks);
    const [clonedProductQty, setClonedProductQty] = useState("");
    const [isMinimumQtyOpen, setIsMinimumQtyOpen] = useState(false);

    let currentProductSelected = selectedProduct === cartLine?.erpNumber.trim();

    useEffect(() => {
        if (product.qtyOrdered === Number(productQty)) {
            return;
        }
        setProductQty(`${cartLine?.qtyOrdered || ""}`);
        setClonedProductQty(`${cartLine.qtyOrdered || ""}`);
    }, [product.qtyOrdered, cartLine]);




    const debouncedIncreaseQuantity = useDebounce((id: string) => {
        setSelectedProduct(id);
        if (!addToCartLoading) {
            dispatch(
                addToCart({
                    productId: product.id,
                    qtyOrdered: latestAddClicks.current,
                    unitOfMeasure: "",
                    onComplete() {
                        setSelectedProduct("");
                        latestAddClicks.current = 0;
                        setAddClicks(0);
                    },
                }),
            );
        }
    }, 500);

    const debouncedDecreaseQuantity = useDebounce((id: string) => {
        setSelectedProduct(id);
        if (!addToCartLoading) {
            dispatch(
                updateCartLine({
                    cartLine: {
                        ...cartLine,
                        qtyOrdered: Number(Number(clonedProductQty) - latestMinusClicks.current),
                    },
                    onComplete() {
                        setSelectedProduct("");
                        setMinusClicks(0);
                        latestMinusClicks.current = 0;
                    },
                }),
            );
        }
    }, 500);

    if (!product.canAddToCart) {
        return null;
    }

    const keyPressHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.which === 13) {
            (event.target as any).blur();
        }
    };

    const qtyChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setProductQty(value);
    };

    const increaseQuantity = (id: string) => {
        if (!addToCartLoading && !isCartLoading) {
            sessionStorage.setItem("item_list_name", "Buy Now");
            setProductQty(prev => String(Number(prev) + Number(product.properties.minimuM_QTY)));
            setAddClicks(prevClicks => prevClicks + Number(product.properties.minimuM_QTY));
            latestAddClicks.current = addClicks + Number(product.properties.minimuM_QTY);
            debouncedIncreaseQuantity(id);
        }
    };



    const decreaseQuantity = (id: string) => {
        if (!addToCartLoading && !isCartLoading) {
            if (productQty === "1") {
                setSelectedProduct(id);
                dispatch(
                    removeCartLine({
                        cartLineId: cartLine.id,
                        onComplete(result) {
                            setSelectedProduct("");
                        },
                    }),
                );
                if (cartLine) {
                    sessionStorage.setItem("item_list_name", "Buy Now");
                    removeProductFromCart(cartLine, "Buy Now", session);
                }
            } else {
                setMinusClicks(prevClicks => prevClicks + Number(product.properties.minimuM_QTY));
                latestMinusClicks.current = minusClicks + Number(product.properties.minimuM_QTY);
                setProductQty(prev => String(Number(prev) - parseInt(product.properties.minimuM_QTY)));
                debouncedDecreaseQuantity(id);
                if (cartLine) {
                    sessionStorage.setItem("item_list_name", "Buy Now");
                    const product: any = cartLine;

                    removeProductFromCart(product, "Buy Now", session);
                }
            }
        }
    };

    const addLoader = (type: "counter" | "textfield" = "counter") => {
        if (type === "counter") {
            return (
                <StyledWrapper
                    {...(variant === "product listing list" ? fieldStyles.loadingList : fieldStyles.loading)}
                >
                    {/* added hardcode image for image will move it to static folder and access it from there */}
                    <Img
                        alt="loader"
                        src="https://cdn.adiglobaldistribution.us/userfile-na/US/userfiles/Images/Themes/icons/rd-addtocart-loader.gif"
                    />
                </StyledWrapper>
            );
        }
        return (
            <StyledWrapper
                {...(variant === "product listing grid" ? fieldStyles.fieldLoading : fieldStyles.fieldLoadingList)}
            >
                {/* added hardcode image for image will move it to static folder and access it from there */}
                <Img
                    alt="loader"
                    src="https://cdn.adiglobaldistribution.us/userfile-na/US/userfiles/Images/Themes/icons/rd-addtocart-loader.gif"
                />
            </StyledWrapper>
        );
    };

    const updateQuantity = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;

        if (!value && value !== "0") {
            setProductQty(String(cartLine.qtyOrdered));
            return;
        }
        if (value === "0") {
            setUpdateQuantityLoader(true);
            dispatch(
                removeCartLine({
                    cartLineId: cartLine.id,
                    onComplete(result) {
                        setUpdateQuantityLoader(false);
                    },
                }),
            );
            sessionStorage.setItem("item_list_name", "Buy Now");
            addOrRemoveCart(cartLine, 0, session);
            return;
        }
        if (Boolean(value) && Number(value) && cartLine.qtyOrdered !== Number(value)) {
            if (!checkAllowedQuantity(Number(value), Number(product.properties.minimuM_QTY))) {
                if (cartLine && cartLine.qtyOrdered) {
                    setProductQty(cartLine.qtyOrdered?.toString());
                } else {
                    setProductQty(product.properties.minimuM_QTY);
                }
                setIsMinimumQtyOpen(true);
                return;
            }
            setUpdateQuantityLoader(true);
            dispatch(
                updateCartLine({
                    cartLine: {
                        ...cartLine,
                        qtyOrdered: Number(event.target.value),
                    },
                    onComplete(result) {
                        setUpdateQuantityLoader(false);
                    },
                }),
            );
            sessionStorage.setItem("item_list_name", "Buy Now");
            const qty = Number(event.target.value);
            addOrRemoveCart(cartLine, qty, session);
        }
    };

    return (
        <>
            {variant === "product listing list" && (
                <StyledWrapper {...fieldStyles.containerList}>
                    {(updateCartLoading || Object.keys(removeCartLineLoading).length) && currentProductSelected ? (
                        addLoader("counter")
                    ) : (
                        <StyledWrapper
                            {...fieldStyles.iconsList}
                            className="minusIcon"
                            onClick={() => decreaseQuantity(product.erpNumber.trim())}
                        >
                            {productQty === "1" ? <Trash2 /> : <Minus />}
                        </StyledWrapper>
                    )}

                    {!updateQuantityLoader ? (
                        <StyledWrapper {...fieldStyles.field}>
                            <TextField
                                value={productQty}
                                type="text"
                                min={0}
                                max={999999}
                                maxLength={5}
                                disabled={updateCartLoading || addToCartLoading}
                                data-test-selector="product_qtyOrdered"
                                {...otherProps}
                                onKeyPress={keyPressHandler}
                                onChange={qtyChangeHandler}
                                onBlur={updateQuantity}
                                {...styles}
                            />
                        </StyledWrapper>
                    ) : (
                        addLoader("textfield")
                    )}
                    {(addToCartLoading || isCartLoading) && currentProductSelected ? (
                        addLoader("counter")
                    ) : (
                        <StyledWrapper
                            onClick={() => increaseQuantity(product.erpNumber.trim())}
                            {...fieldStyles.iconsList}
                            className="plusIcon"
                        >
                            <Plus />
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            )}
        </>
    );
};

export default connect(mapStateToProps)(ADIProductQuantityOrdered);
